import {inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {MatSnackBar} from '@angular/material/snack-bar';
import {TranslateService} from '@ngx-translate/core';
import {MatDialog} from '@angular/material/dialog';
import {DialogData} from '../shared/interfaces/data.interfaces';
import {FormValueTypeEnum} from '../shared/enums/data.enums';
import {DialogService} from './dialog.service';
import Swal from 'sweetalert2';

export type ToastType = 'success' | 'error' | 'warning' | 'normal';

export interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  router = inject(Router);
  snackbar = inject(MatSnackBar);
  translate = inject(TranslateService);
  dialogService = inject(DialogService);
  dialog = inject(MatDialog);

  // Toasts render via ToastContainerComponent (mounted once, globally, in
  // AppComponent) instead of MatSnackBar — MatSnackBar only ever shows one
  // instance at a time and immediately dismisses it to show the next, so
  // e.g. a "Sale completed" toast was getting overwritten mid-read by the
  // "Cart cleared" toast that fired right after it. This array lets any
  // number of toasts stack and each run out its own duration independently.
  toasts: Toast[] = [];
  private nextToastId = 1;
  private readonly toastTimers = new Map<number, ReturnType<typeof setTimeout>>();

  private pushToast(message: string, type: ToastType, duration: number): void {
    const id = this.nextToastId++;
    this.toasts.push({ id, message, type });
    const timer = setTimeout(() => this.dismissToast(id), duration);
    this.toastTimers.set(id, timer);
  }

  dismissToast(id: number): void {
    this.toasts = this.toasts.filter(t => t.id !== id);
    const timer = this.toastTimers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.toastTimers.delete(id);
    }
  }

  successMessage(message: string): void {
    this.successSnackBar(message);
  }

  warningMessage(message: string): void {
    this.warningSnackBar(message);
  }

  errorMessage(message: string): void {
    this.errorSnackBar(message);
  }

  message(message: string): void {
    this.normalSnackBar(message);
  }

  successMessageAndCloseModals(message: string): void {
    this.successSnackBar(message);
    this.dialog.closeAll();
  }

  errorMessageAndCloseModals(message: string): void {
    this.errorSnackBar(message);
    this.dialog.closeAll();
  }

  async successMessageWithRedirect(message: string, route: string): Promise<any> {
    this.successSnackBar(message);
    return this.router.navigate([route]);
  }

  async errorMessageWithRedirect(message: string, route: string): Promise<any> {
    this.errorSnackBar(message);
    return this.router.navigate([route]);
  }

  copySnackBar(
    message, closeText = 'Dismiss', duration = 20000,
    verticalPosition: any = 'bottom', horizontalPosition: any = 'right'): void {
    this.snackbar.open(message, closeText, {
      duration,
      verticalPosition,
      horizontalPosition
    });
  }

  private normalSnackBar(message, closeText = 'Dismiss', duration = 5000, verticalPosition: any = 'bottom', horizontalPosition: any = 'right'): void {
    this.pushToast(message, 'normal', duration);
  }

  private warningSnackBar(message, closeText = 'Dismiss', duration = 5000, verticalPosition: any = 'bottom', horizontalPosition: any = 'right'): void {
    this.pushToast(message, 'warning', duration);
  }

  private successSnackBar(message, closeText = 'Dismiss', duration = 3000, verticalPosition: any = 'bottom', horizontalPosition: any = 'right'): void {
    this.pushToast(message, 'success', duration);
  }

  private errorSnackBar(
    message, closeText = 'Dismiss', duration = 5000,
    verticalPosition: any = 'bottom', horizontalPosition: any = 'right'): void {
    this.pushToast(message, 'error', duration);
  }

  /**
   * @deprecated The method should not be used, use confirmation from DialogService instead, will be removed on next version
   */
  confirmation(message: string, confirmButtonText?: string) {
    // if (confirmButtonText === null){
    //   confirmButtonText = 'yes';
    // }
    const input: DialogData = {
      message,
      buttons: (confirmButtonText !== null) ? [ { title: 'cancel', value: false}, { title: confirmButtonText, value: true}] : []
    };

    return this.dialogService.confirmation(input);
  }

  /**
   * @deprecated The method should not be used, use confirmation from DialogService instead, will be removed on next version
   */
  confirmationWithInput(message: string,  confirmButtonText?: string, placeholder?: string) {
    const input: DialogData = {
      message,
      placeholder,
      isForm: true,
      formFieldType: FormValueTypeEnum.TEXT,
      buttons: (confirmButtonText !== null) ? [ { title: 'cancel', value: false}, { title: confirmButtonText, value: true}] : []
    };

    return this.dialogService.confirmation(input);
  }

  confirmationSweetAlert(
    message: string = 'Are you sure?',
    confirmButtonText: string = 'YES'
  ) {
    return Swal.fire({
      title: message,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText,
      allowOutsideClick: false,

    });
  }

  confirmAlert(message: string, title: string = 'Are you Sure ?') {
    return Swal.fire({
      title,
      text: message,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Yes',

    });
  }



}
